import { find } from 'lodash';
import { Component, Input } from '@angular/core';
import { map, repeat, shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';

import { CartService } from '@core/services/cart/cart.service';
import { OrdersService } from '@core/services/orders/orders.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { ZerosixService } from '@core/services/zerosix/zerosix.service';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { MarketplaceStoreRefiner } from '@common/refiners/marketplace.refiner';
import { IZerosixAsset, ZerosixAssetType } from '@core/models/zerosix.model';
import {
  ZerosixOfferedProductPickerModalComponent,
  ZerosixOfferedProductPickerModalConfig,
} from '@shared/modals/zerosix/zerosix-offered-product-picker-modal/zerosix-offered-product-picker-modal.component';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { combineLatest, EMPTY, Subject } from 'rxjs';
import { statedStream } from '@shared/utils/rxjs/stated-steam';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { opacityAnimation } from '@shared/animations/opacity.animation';

@Component({
  selector: 'app-zerosix-asset-list-block',
  templateUrl: './zerosix-asset-list-block.component.html',
  animations: [opacityAnimation],
})
export class ZerosixAssetListBlockComponent extends DestroyerBase {
  @Input() heading = 'como.loyalty-rewards';
  @Input() subheading?: string;
  @Input() width: 'centered' | 'full' = 'centered';
  @Input() itemsToShowCardCarousel = 4;
  @Input() headingColor: IContentBuilderFieldColor = {
    value: 'neutral-800',
    type: ColorFieldTypesValues.Palette,
  };
  @Input() textColor: IContentBuilderFieldColor = {
    value: 'neutral-600',
    type: ColorFieldTypesValues.Palette,
  };
  @Input() assetNameColor: IContentBuilderFieldColor = {
    value: 'neutral-800',
    type: ColorFieldTypesValues.Palette,
  };
  @Input() assetDetailsColor: IContentBuilderFieldColor = {
    value: 'neutral-800',
    type: ColorFieldTypesValues.Palette,
  };

  /**
   * Used to refresh the user account when it creates his account and validates his registration (at this time, this component, might already loaded)
   */
  protected readonly refreshZerosixAccountInfo$ = new Subject<void>();

  protected readonly selectIsZeroSixPluginEnabled =
    this.marketplaceRefiner.selectIsZeroSixPluginEnabled;

  private readonly hasUserHaveZerosixAccount$ =
    this.zerosixService.isUserZeroSixCardEnabledOnMarketplaceLevel$();

  protected readonly userCanLoadZerosix$ = combineLatest([
    this.hasUserHaveZerosixAccount$,
    this.selectIsZeroSixPluginEnabled,
  ]).pipe(
    map(([hasUserHaveZerosixAccount, isZeroSixPluginEnabled]) => {
      return hasUserHaveZerosixAccount && isZeroSixPluginEnabled;
    }),
  );

  protected readonly memberCard$ = this.hasUserHaveZerosixAccount$.pipe(
    takeUntil(this._destroyerRef),
    switchMap(hasUserHaveZerosixAccount =>
      hasUserHaveZerosixAccount ? statedStream(this.zerosixService.getCard$()) : EMPTY,
    ),
    repeat({
      delay: () => this.refreshZerosixAccountInfo$,
    }),
    shareReplay(1),
  );
  protected readonly assets$ = this.memberCard$.pipe(
    map(memberCardData => {
      if (memberCardData.isLoading) {
        return [];
      }
      const memberCard = memberCardData.result;
      if (!memberCard) return [];
      const vouchers = memberCard.available_vouchers;
      vouchers.forEach(voucher => {
        voucher.type = ZerosixAssetType.VOUCHER;
        voucher.is_usable = true;
      });

      const rewards = memberCard.rewards;
      rewards.forEach(reward => {
        reward.type = ZerosixAssetType.REWARD;
      });

      return [...vouchers, ...rewards];
    }),
  );
  compartSlides!: IZerosixAsset[][];
  zerosixAssetType = ZerosixAssetType;

  isDesktop$ = this.settingsSelector.selectDeviceIsDesktopScreen;

  constructor(
    private readonly cartService: CartService,
    private readonly ordersService: OrdersService,
    private readonly modalsService: ModalsService<ZerosixOfferedProductPickerModalConfig>,
    private readonly zerosixService: ZerosixService,
    protected settingsSelector: SettingsStoreSelector,
    private marketplaceRefiner: MarketplaceStoreRefiner,
  ) {
    super();
    this.modalsService
      .event$(ZerosixOfferedProductPickerModalComponent.handle)
      .pipe(takeUntilDestroyed())
      .subscribe(event => {
        if (event.type === 'productSelected') {
          this.updateCart();
        }
      });
  }

  useAsset(asset: IZerosixAsset): void {
    const coupon = this.zerosixService.calculateCouponFromAsset(asset);
    if (!coupon) {
      return;
    }
    this.cartService.addCoupon(coupon);
    if (asset.external_ids.length > 0) {
      this.modalsService.open(ZerosixOfferedProductPickerModalComponent.handle, undefined, {
        asset,
      });
    } else {
      this.updateCart();
    }
  }

  removeAsset(asset: IZerosixAsset): void {
    const coupon = this.zerosixService.calculateCouponFromAsset(asset);
    if (!coupon) {
      return;
    }
    this.cartService.removeCoupon(coupon);
    this.updateCart();
  }

  getAssetTag(voucher: IZerosixAsset): string {
    let description = '';

    if (voucher.value) {
      description += '– ' + voucher.value + ' ';
    }
    if (voucher.unit) {
      description += voucher.unit + ' ';
    }
    return description.trim();
  }

  isAssetInCart(voucher: IZerosixAsset): boolean {
    const coupons = this.cartService.getCouponsInCart();

    return !!find(
      coupons,
      coupon =>
        (coupon.code && coupon.code === voucher.code) ||
        (coupon.key && coupon.key === 'reward_' + voucher.id),
    );
  }

  private updateCart(): void {
    this.ordersService.checkCartIsValid$().pipe(take(1)).subscribe();
  }
}
